import { Box, Grid, Typography, Link, Chip, Divider, useMediaQuery, Button } from '@mui/material';
import {
    Translate,
    NewReleases,
    Android,
    GitHub,
    Language,
    MenuBook,
    School,
    Update,
    Article,
    Download,
    Favorite,
    Devices
} from '@mui/icons-material';

function Footer() {
    const isNonMobileScreens = useMediaQuery("(min-width: 900px)");

    return (
        <Box
            component="footer"
            sx={{
                background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.4) 0%, rgba(10, 15, 30, 0.95) 100%)',
                backdropFilter: 'blur(12px)',
                borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                color: '#E2E8F0',
                pt: { xs: 5, md: 7 },
                pb: { xs: 4, md: 5 },
                px: { xs: 3, md: 8 },
                mt: 6
            }}
        >
            <Box sx={{ maxWidth: '1280px', margin: '0 auto' }}>
                {/* Feature Highlight Banner */}
                <Box
                    sx={{
                        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(147, 51, 234, 0.12) 100%)',
                        border: '1px solid rgba(255, 255, 255, 0.12)',
                        borderRadius: '16px',
                        p: { xs: 3, md: 4 },
                        mb: 6,
                        textAlign: 'center',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
                    }}
                >
                    <Chip
                        icon={<NewReleases sx={{ color: '#60A5FA !important' }} />}
                        label="UNIVERSAL LEARNING PLATFORM"
                        sx={{
                            mb: 2,
                            fontWeight: 700,
                            letterSpacing: '0.5px',
                            backgroundColor: 'rgba(59, 130, 246, 0.2)',
                            color: '#93C5FD',
                            border: '1px solid rgba(147, 197, 253, 0.3)'
                        }}
                    />
                    <Typography variant="h5" fontWeight="800" sx={{ color: '#FFFFFF', mb: 1.5 }}>
                        Master Any Subject with Üben
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            maxWidth: '750px',
                            margin: '0 auto',
                            color: '#94A3B8',
                            fontSize: { xs: '0.95rem', md: '1.05rem' },
                            lineHeight: 1.6
                        }}
                    >
                        Intelligent spaced repetition (FSRS) for any discipline — languages, sciences, coding, history, and medical decks.
                        Import Anki <code style={{ background: 'rgba(255,255,255,0.1)', padding: '2px 6px', borderRadius: '4px' }}>.apkg</code> files or study offline on your Kindle.
                    </Typography>
                </Box>

                {/* Grid Links Section */}
                <Grid container spacing={4} justifyContent="space-between">
                    {/* Column 1: Brand & Creator */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="h6" fontWeight="800" sx={{ color: '#FFFFFF', mb: 1.5, letterSpacing: '-0.5px' }}>
                            Üben
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#94A3B8', mb: 2.5, lineHeight: 1.6 }}>
                            A modern, free spaced repetition platform built for effective learning on Web and Kindle.
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                            <Link
                                href="https://kurizu.vercel.app/"
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="none"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    color: '#CBD5E1',
                                    fontSize: '0.9rem',
                                    transition: 'color 0.2s ease',
                                    '&:hover': { color: '#60A5FA' }
                                }}
                            >
                                <img src="/assets/website.png" alt="Website" style={{ width: '18px', height: '18px', borderRadius: '50%' }} />
                                Created by <strong>Kurizu</strong>
                            </Link>

                            <Link
                                href="https://github.com/crizmo"
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="none"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    color: '#CBD5E1',
                                    fontSize: '0.9rem',
                                    transition: 'color 0.2s ease',
                                    '&:hover': { color: '#60A5FA' }
                                }}
                            >
                                <GitHub sx={{ fontSize: '18px' }} />
                                GitHub Profile
                            </Link>
                        </Box>
                    </Grid>

                    {/* Column 2: Quick Links & Mobile App */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="subtitle1" fontWeight="700" sx={{ color: '#FFFFFF', mb: 2 }}>
                            Quick Links
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {/* Highlighted Android App Button */}
                            <Button
                                component="a"
                                href="/uben.apk"
                                download="uben.apk"
                                startIcon={<Android sx={{ color: '#4ADE80' }} />}
                                endIcon={<Download sx={{ fontSize: '16px !important' }} />}
                                sx={{
                                    justifyContent: 'flex-start',
                                    backgroundColor: 'rgba(74, 222, 128, 0.1)',
                                    color: '#4ADE80',
                                    border: '1px solid rgba(74, 222, 128, 0.3)',
                                    borderRadius: '8px',
                                    px: 2,
                                    py: 0.8,
                                    fontSize: '0.875rem',
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    '&:hover': {
                                        backgroundColor: 'rgba(74, 222, 128, 0.2)',
                                        borderColor: '#4ADE80',
                                        transform: 'translateY(-1px)'
                                    },
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                Android App (.apk)
                            </Button>

                            <Link href="/kanki" underline="none" sx={{ color: '#94A3B8', display: 'flex', alignItems: 'center', gap: 1, '&:hover': { color: '#60A5FA' } }}>
                                <Devices sx={{ fontSize: '18px' }} />
                                KAnki for Kindle
                            </Link>
                            <Link href="/courses" underline="none" sx={{ color: '#94A3B8', display: 'flex', alignItems: 'center', gap: 1, '&:hover': { color: '#60A5FA' } }}>
                                <School sx={{ fontSize: '18px' }} />
                                Step-by-Step Courses
                            </Link>
                            <Link href="/blog" underline="none" sx={{ color: '#94A3B8', display: 'flex', alignItems: 'center', gap: 1, '&:hover': { color: '#60A5FA' } }}>
                                <Article sx={{ fontSize: '18px' }} />
                                Blog & Study Tips
                            </Link>
                            <Link href="/updates" underline="none" sx={{ color: '#94A3B8', display: 'flex', alignItems: 'center', gap: 1, '&:hover': { color: '#60A5FA' } }}>
                                <Update sx={{ fontSize: '18px' }} />
                                Updates & Roadmap
                            </Link>
                        </Box>
                    </Grid>

                    {/* Column 3: Learn Languages */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="subtitle1" fontWeight="700" sx={{ color: '#FFFFFF', mb: 2 }}>
                            Learn Languages
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                            <Link href="/learn/german" underline="none" sx={{ color: '#94A3B8', '&:hover': { color: '#60A5FA' } }}>🇩🇪 Learn German</Link>
                            <Link href="/learn/japanese" underline="none" sx={{ color: '#94A3B8', '&:hover': { color: '#60A5FA' } }}>🇯🇵 Learn Japanese</Link>
                            <Link href="/learn/french" underline="none" sx={{ color: '#94A3B8', '&:hover': { color: '#60A5FA' } }}>🇫🇷 Learn French</Link>
                            <Link href="/learn/spanish" underline="none" sx={{ color: '#94A3B8', '&:hover': { color: '#60A5FA' } }}>🇪🇸 Learn Spanish</Link>
                            <Link href="/learn/korean" underline="none" sx={{ color: '#94A3B8', '&:hover': { color: '#60A5FA' } }}>🇰🇷 Learn Korean</Link>
                            <Link href="/learn" underline="none" sx={{ color: '#60A5FA', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, '&:hover': { textDecoration: 'underline' } }}>
                                <Translate sx={{ fontSize: '16px' }} /> All Language Guides →
                            </Link>
                        </Box>
                    </Grid>

                    {/* Column 4: Community & Support */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="subtitle1" fontWeight="700" sx={{ color: '#FFFFFF', mb: 2 }}>
                            Community & Support
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Link
                                href="https://discord.gg/JtcrpG7ECA"
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="none"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1.2,
                                    backgroundColor: '#5865F2',
                                    color: '#FFFFFF',
                                    px: 2,
                                    py: 1,
                                    borderRadius: '8px',
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                    transition: 'all 0.2s ease',
                                    '&:hover': { backgroundColor: '#4752C4', transform: 'translateY(-1px)' }
                                }}
                            >
                                <img src="/assets/discord.png" alt="Discord" style={{ width: '20px', height: '20px' }} />
                                Join Discord Server
                            </Link>

                            <Link
                                href="https://ko-fi.com/kurizu"
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="none"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1.2,
                                    backgroundColor: '#13C3FF',
                                    color: '#FFFFFF',
                                    px: 2,
                                    py: 1,
                                    borderRadius: '8px',
                                    fontWeight: 700,
                                    fontSize: '0.875rem',
                                    transition: 'all 0.2s ease',
                                    '&:hover': { backgroundColor: '#00A8E0', transform: 'translateY(-1px)' }
                                }}
                            >
                                <img src="/assets/support/kofi.png" alt="Ko-fi" style={{ width: '22px', height: '17px' }} />
                                Support on Ko-fi
                            </Link>

                            <Link
                                href="https://www.producthunt.com/products/uben"
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="none"
                            >
                                <img
                                    alt="Uben on Product Hunt"
                                    width="200"
                                    height="43"
                                    loading="lazy"
                                    src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1093333&theme=dark"
                                    style={{ borderRadius: '6px' }}
                                />
                            </Link>
                        </Box>
                    </Grid>
                </Grid>

                {/* Bottom Divider & Copyright */}
                <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.08)' }} />

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                        color: '#64748B',
                        fontSize: '0.875rem'
                    }}
                >
                    <Typography variant="body2" color="inherit">
                        © {new Date().getFullYear()} Üben. Built with passion for autonomous learning.
                    </Typography>
                    <Typography variant="body2" color="inherit">
                        Free & Open Community Project
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

export default Footer;
